<template>
    <slot v-bind="{
        ...state,
        venues,
        setVenuesPopover,
        setMonthElements,
        setSidebarDateSelector,
        setDialogDateSelector,
        setDateSelectorDialog,
        openDateSelectorDialog,
        closeDateSelectorDialog,
        setFiltersDialog,
        openFiltersDialog,
        closeFiltersDialog,
        setArtistProgramDialog,
        openArtistProgramDialog,
        closeArtistProgramDialog,
        updateSelectedVenue,
        setSearchQuery,
        setSelectedDate,
        setSelectedPrice,
        updateSelectedView,
        updateOnlyShowFavorited,
        updateFacet,
        setSelectedSeason,
    }" />
</template>

<script setup>
import { nextTick, onBeforeMount, onBeforeUnmount, reactive } from 'vue';
import { get, cloneDeep, flatten, uniq, map, max, throttle, orderBy, debounce } from 'lodash';
import Fuse from 'fuse.js';
import { endOfDay, format, fromUnixTime, isValid, isWithinInterval, parse, startOfDay } from 'date-fns';

import calendarVenuesPopover from '../utils/calendar-venues-popover';
import { getInstancesForSeason, getSeasons, getUpcomingInstances, updateFavoriteFilter } from '../services/inventory';
import { buttonStates, bookingStatuses, getPerformanceBookingState } from '../services/booking';
import waitForScroll from '../utils/wait-for-scroll';
import getFirstFocusableElement from '../utils/get-first-focusable-element';
import zonedDate from '../utils/zoned-date';
import favorites from '../services/favorites';
import EventBus from '../utils/event-bus';

const venues = [
    {
        value: 'all',
        label: 'All Venues',
    },
    {
        value: 'laphil',
        label: 'Walt Disney Concert Hall',
    },
    {
        value: 'hollywoodbowl',
        label: 'Hollywood Bowl',
    },
    {
        value: 'theford',
        label: 'The Ford',
    },
];

const customSort = {
    Monday: 0,
    Tuesday: 1,
    Wednesday: 2,
    Thursday: 3,
    Friday: 4,
    Saturday: 5,
    Sunday: 6,
}

const props = defineProps({
    defaultVenue: {
        type: String,
        default: 'all',
    },
})

const state = reactive({
    isLoading: true,
    hasError: false,
    searchQuery: '',
    selectedView: 'list',
    selectedVenue: venues[0],
    seasons: [],
    upcomingSeasons: [],
    pastSeasons: [],
    selectedSeason: null,
    results: [],
    filters: [],
    totalResults: 0,
    canReset: false,
    minPrice: null,
    maxPrice: null,
    selectedPrice: null,
});

let instances = null;
let fuse = null;
let monthElements = [];
let disableCalendarScroll = false;
let sidebarDateSelector = null;
let dialogDateSelector = null;
let dateSelectorDialog = null;
let activeMonthId = null;
let filtersDialog = null;
let artistProgramDialog = null;

const loadSeasons = () => {
    return getSeasons().then(response => {
        response = orderBy(response, item => +item.startDate, 'desc');

        const all = [
            {
                value: 'upcoming',
                label: 'Upcoming Events',
                venue: 'all',
            }
        ];

        for (const season of response) {
            if (!season.hasEvents) {
                continue;
            }

            all.push({
                id: season.id,
                value: season.slug,
                label: season.title,
                venue: season.tenant.slug,
                isInPast: season.isInPast,
            })
        }

        state.seasons = all;

        state.upcomingSeasons = all.filter(season => {
            return !season.isInPast && season.venue !== 'all';
        });

        state.pastSeasons = all.filter(season => {
            return season.isInPast && season.venue !== 'all';
        });
    });
}

const resolveSeasonAndVenue = (season, venue) => {
    // Determine season
    const validSeason =  state.seasons.find(item => {
        return season && item.value === season;
    });

    state.selectedSeason = validSeason || state.seasons[0];

    // Determine venue
    if (state.selectedSeason.value === 'upcoming') {
        // upcoming → venue param allowed
        const validVenue = venues.find(item => {
            return venue && item.value === venue;
        });

        state.selectedVenue = validVenue || venues[0];
    } else {
        // season → venue is forced by season
        const validVenue = venues.find(item => {
            return item.value === state.selectedSeason.venue;
        });

        state.selectedVenue = validVenue || venues[0];
    }
}

const applySearchParams = initalLoad => {
    const searchParams = new URLSearchParams(window.location.search);

    state.filters = state.filters.map(filter => {
        filter.facets = filter.facets.map(facet => {
            facet.isChecked = searchParams.getAll(filter.name).indexOf(facet.title) !== -1;

            return facet;
        });

        if (initalLoad) {
            filter.isOpen = filter.facets.some(facet => {
                return facet.isChecked;
            });
        }

        return filter;
    });

    if (searchParams.has('showFavorited') && searchParams.get('showFavorited').toLowerCase() === 'yes') {
        state.onlyShowFavorited = true;
    }

    if (searchParams.has('priceFrom')) {
        state.selectedPrice = parseInt(searchParams.get('priceFrom'), 10) || state.minPrice;
    }

    processResults();
}

const updateSearchParams = () => {
    const searchParams = new URLSearchParams();

    for (const filter of state.filters) {
        for (const facet of filter.facets) {
            if (facet.isChecked) {
                searchParams.append(filter.name, facet.title);
            }
        }
    }

    if (state.selectedSeason) {
        searchParams.set('season', state.selectedSeason.value);
    }

    if (state.selectedVenue) {
        searchParams.set('venue', state.selectedVenue.value);
    }

    if (state.onlyShowFavorited) {
        searchParams.set('showFavorited', 'yes');
    }

    if (state.selectedPrice) {
        searchParams.set('priceFrom', state.selectedPrice);
    }

    const url = new URL(window.location);

    url.search = searchParams.toString();

    window.history.pushState(null, '', url.toString());
}

const handlePopStateNavigation = () => {
    const params = new URLSearchParams(window.location.search);

    const season = params.get('season');
    const venue  = params.get('venue');

    const previousSeason = state.selectedSeason?.value;

    resolveSeasonAndVenue(season, venue);

    if (state.selectedSeason?.value !== previousSeason) {
        return loadFeedAndGenerateFilters(false);
    }

    applySearchParams(false);
}

const setupFilters = () => {
    const items = [
        {
            name: 'genres',
            title: 'Genre',
            field: 'filters.genres',
            facets: [],
            isOpen: false,
        },
        {
            name: 'categories',
            title: 'Category',
            field: 'filters.categories',
            facets: [],
            isOpen: false,
        },
        {
            name: 'dayOfWeek',
            title: 'Day of the Week',
            field: 'filters.dayOfWeek',
            facets: [],
            isOpen: false,
        },
        {
            name: 'timeOfDay',
            title: 'Time of Day',
            field: 'filters.timeOfDay',
            facets: [],
            isOpen: false,
        },
    ];

    state.filters = items.map(filter => {
        let facets = instances.map(item => {
            return cloneDeep(get(item, filter.field));
        }).filter(facet => facet);

        facets = flatten(facets);

        facets = uniq(facets);

        filter.facets = facets.map(facet => {
            return {
                title: facet,
                isChecked: false,
                isDisabled: false,
            }
        });

        filter.facets = orderBy(filter.facets, facet => {
            if (facet.title in customSort) {
                return customSort[facet.title];
            }

            return facet.title;
        }, 'asc');

        return filter;
    });
}

const updateFuse = () => {
    fuse = new Fuse(instances, {
        threshold: 0.30,
        useExtendedSearch: true,
        includeScore: false,
        shouldSort: false,
        keys: [
            'tenant.slug',
            'title',
            'event.title',
            'artists.name',
            'program.title',
            'program.arrangers.name',
            'program.composers.name',
            'filters.genres',
            'filters.categories',
            'filters.dayOfWeek',
            'filters.timeOfDay',
            'filters.isFavorited',
            'filters.priceFrom',
        ],
    });
}

const buildQuery = filters => {
    let query = map(filters, (facets, filter) => {
        if (!facets.length) {
            return null;
        }

        return {
            '$or': facets.map(facet => {
                return {
                    [filter]: `="${facet}"`,
                }
            }),
        }
    });

    query = query.filter(item => {
        return item !== null;
    });

    if (state.selectedVenue && state.selectedVenue.value !== 'all') {
        query.push({
            '$or': [
                {
                    'tenant.slug': `=${state.selectedVenue.value}`,
                },
            ],
        });
    }

    if (state.selectedPrice && state.minPrice !== state.maxPrice) {
        const len = state.maxPrice - state.selectedPrice + 1;
        const prices = new Array(len);

        for (let i = 0; i < len; i++) {
            prices[i] = { 'filters.priceFrom': `=${state.selectedPrice + i}` };
        }

        if (prices.length) {
            query.push({
                '$or': prices,
            });
        }
    }

    const searchQuery = state.searchQuery.trim();

    if (searchQuery) {
        query.push({
            '$or': [
                {
                    'event.title': searchQuery,
                },
                {
                    'artists.name': searchQuery,
                },
                {
                    'program.title': searchQuery,
                },
                {
                    'program.arrangers.name': searchQuery,
                },
                {
                    'program.composers.name': searchQuery,
                },
            ],
        });
    }

    if (state.onlyShowFavorited) {
        query.push({
            'filters.isFavorited': `="Yes"`,
        });
    }

    return query;
}

const handleScroll = throttle(event => {
    if (disableCalendarScroll) {
        return;
    }

    let activeElement = monthElements.find(element => {
        const rect = element.getBoundingClientRect();

        return rect.top <= 0 && rect.bottom > 0;
    });

    if (!activeElement && monthElements.length) {
        activeElement = monthElements[0];
    }

    if (activeElement) {
        activeMonthId = activeElement.dataset.month;

        if (sidebarDateSelector) {
            sidebarDateSelector.goToMonth(activeElement.dataset.month);
        }

        if (dialogDateSelector) {
            dialogDateSelector.goToMonth(activeElement.dataset.month);
        }
    }
}, 200);

const updateCalendarScroll = () => {
    window.removeEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScroll);
}

const processResults = () => {
    const appliedFacets = {}

    for (const filter of state.filters) {
        appliedFacets[filter.field] = [];

        for (const facet of filter.facets) {
            if (facet.isChecked) {
                appliedFacets[filter.field].push(facet.title);
            }
        }
    }

    const appliedQuery = buildQuery(appliedFacets);

    let results = instances;
    let canReset = false;

    if (appliedQuery.length) {
        results = fuse.search({
            '$and': appliedQuery,
        }).map(result => {
            return result.item;
        });

        canReset = true;
    }

    state.results = results;
    state.canReset = canReset;

    state.totalResults = state.results.length;

    state.filters = state.filters.map(filter => {
        filter.facets = filter.facets.map(facet => {
            if (!facet.isChecked) {
                const potentialFacets = cloneDeep(appliedFacets);

                potentialFacets[filter.field] = [facet.title];

                const potentialResults = fuse.search({
                    '$and': buildQuery(potentialFacets),
                });

                facet.isDisabled = !potentialResults.length;
            }

            return facet;
        });

        return filter;
    });

    updateCalendarScroll();
}

const loadFeedAndGenerateFilters = (initalLoad = false, startDate = null, endDate = null) => {
    state.isLoading = true;
    state.hasError = false;

    let promise = null;

    if (state.selectedSeason.value === 'upcoming') {
        promise = getUpcomingInstances();
    } else {
        promise = getInstancesForSeason(state.selectedSeason.id);
    }

    return promise.then(async response => {
        instances = response.filter(item => {
            return !item.hide_from_calendar;
        });

        if (startDate && endDate) {
            let realStartDate = parse(startDate, 'yyyyMMdd', new Date());
            let realEndDate = parse(endDate, 'yyyyMMdd', new Date());

            if (!isValid(realStartDate) || !isValid(realEndDate)) {
                realStartDate = fromUnixTime(startDate);
                realEndDate = fromUnixTime(endDate);
            }

            if (isValid(realStartDate) && isValid(realEndDate)) {
                const interval = {
                    start: startOfDay(zonedDate(realStartDate)),
                    end: endOfDay(zonedDate(realEndDate)),
                }

                instances = instances.filter(instance => {
                    return isWithinInterval(instance.startDate, interval);
                });
            }
        }

        for (const instance of instances) {
            if (instance.startDate > zonedDate() && !instance.customBooking.bookingUrl) {
                instance.bookingState = await getPerformanceBookingState(instance.tessitura.performanceId);
            } else {
                instance.bookingState = {
                    status: bookingStatuses.offSale,
                }
            }
        }

        const pricesFrom = instances.map(instance => {
            return instance.filters.priceFrom;
        });

        const maxPrice = max(pricesFrom);

        if (maxPrice) {
            state.minPrice = 0;
            state.maxPrice = max(pricesFrom);
        }

        state.selectedPrice = 0;

        setupFilters();

        updateFuse();

        applySearchParams(initalLoad);

        state.hasError = false;
    }).catch(error => {
        console.log(error);
        state.hasError = true;
    }).finally(() => {
        state.isLoading = false;
    });
}

const setVenuesPopover = element => {
    if (element) {
        calendarVenuesPopover(element);
    }
}

const setMonthElements = element => {
    if (element) {
        monthElements.push(element);
    } else {
        // Vue calls with null for any element being unmounted
        // So we must clean the array each time
        monthElements = monthElements.filter(node => {
            return document.body.contains(node);
        });
    }
}

const setSidebarDateSelector = element => {
    sidebarDateSelector = element;
}

const setDialogDateSelector = element => {
    dialogDateSelector = element;
}

const setDateSelectorDialog = element => {
    dateSelectorDialog = element;
}

const openDateSelectorDialog = () => {
    dateSelectorDialog.showModal();

    if (activeMonthId) {
        const activeMonthElement = dateSelectorDialog.querySelector(`#${activeMonthId}`);

        if (activeMonthElement) {
            activeMonthElement.scrollIntoView({ block: 'start' });
        }
    }
}

const closeDateSelectorDialog = () => {
    dateSelectorDialog.close();
}

const setFiltersDialog = element => {
    filtersDialog = element;
}

const openFiltersDialog = () => {
    filtersDialog.showModal();
}

const closeFiltersDialog = () => {
    filtersDialog.close();
}

const setArtistProgramDialog = element => {
    artistProgramDialog = element;
}

const openArtistProgramDialog = instance => {
    state.selectedInstance = instance;

    nextTick(() => {
        artistProgramDialog.showModal();
    });
}

const closeArtistProgramDialog = () => {
    artistProgramDialog.close();

    // The double `requestAnimationFrame` is not a mistake, it's intentional
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            state.selectedInstance = null;
        });
    });
}

const updateSelectedVenue = event => {
    state.selectedVenue = venues.find(venue => {
        return venue.value === event.target.value;
    });

    processResults();
    updateSearchParams();
}

const setSearchQuery = debounce(event => {
    state.searchQuery = event.target.value;

    processResults();
    updateSearchParams();
}, 250);

const setSelectedDate = date => {
    const element = document.querySelector('.day-' + format(date, 'yyyy-MM-dd'));

    if (element) {
        disableCalendarScroll = true;

        dateSelectorDialog.close();

        element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });

        waitForScroll().then(() => {
            getFirstFocusableElement(element)?.focus({ preventScroll: true });

            disableCalendarScroll = false;
        });
    }
}

const setSelectedPrice = event => {
    state.selectedPrice = parseInt(event.target.value, 10);

    processResults();
    updateSearchParams();
}

const updateSelectedView = event => {
    state.selectedView = event.target.value;
}

const updateOnlyShowFavorited = event => {
    state.onlyShowFavorited = event.target.checked;

    processResults();
    updateSearchParams();
}

const updateFacet = (eventFilter, eventFacet, event) => {
    state.filters = state.filters.map(filter => {
        filter.facets = filter.facets.map(facet => {
            if (filter.title === eventFilter.title && facet.title === eventFacet.title) {
                facet.isChecked = event.target.checked;
            }

            return facet;
        });

        return filter;
    });

    processResults();
    updateSearchParams();

    if (event.target.checked) {
        EventBus.emit('announce', { message: `showing events in the ${eventFacet.title} ${eventFilter.title}` });
    }
}

const setSelectedSeason = event => {
    state.selectedSeason = state.seasons.find(season => {
        return season.value === event.target.value;
    });

    resolveSeasonAndVenue(state.selectedSeason.value, state.selectedVenue?.value);

    const searchParams = new URLSearchParams();
    searchParams.set('season', state.selectedSeason.value);

    if (state.selectedSeason.value === 'upcoming') {
        searchParams.set('venue', state.selectedVenue.value);
    }

    const url = new URL(window.location);
    url.search = searchParams.toString();
    window.history.pushState(null, '', url.toString());

    loadFeedAndGenerateFilters(false);
}

const handleFavoritesChange = () => {
    instances = instances.map(instance => {
        updateFavoriteFilter(instance);

        return instance;
    });

    updateFuse();
}

onBeforeMount(() => {
    loadSeasons().then(() => {
        const searchParams = new URLSearchParams(window.location.search);

        const season = searchParams.get('season');
        const venue = searchParams.get('venue');
        const startDate = searchParams.get('startDate');
        const endDate = searchParams.get('endDate');

        resolveSeasonAndVenue(season, venue ?? props.defaultVenue);

        return loadFeedAndGenerateFilters(false, startDate, endDate);
    }).finally(() => {
        window.addEventListener('popstate', () => {
            handlePopStateNavigation();
        });

        window.addEventListener(favorites.favoritePerformancesChangedEventName, handleFavoritesChange);
    });
});

onBeforeUnmount(() => {
    window.removeEventListener(favorites.favoritePerformancesChangedEventName, handleFavoritesChange);
    window.removeEventListener('scroll', handleScroll);
});
</script>
